import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaCheckCircle, FaChevronDown, FaArrowRight } from 'react-icons/fa';
import FooterContact from './FooterContact';
import HomeVideo from '../assets/images/check-video.mp4';
import securityBg from '../assets/images/security-banner.webp';

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay },
    viewport: { once: true },
});

function FAQItem({ q, a, index }) {
    const [open, setOpen] = useState(index === 0);
    return (
        <motion.div
            {...fadeUp(index * 0.08)}
            style={{
                borderRadius: '12px',
                border: open ? '1px solid rgba(59,130,246,0.35)' : '1px solid rgba(255,255,255,0.07)',
                marginBottom: '12px',
                overflow: 'hidden',
                background: open ? 'rgba(59,130,246,0.06)' : 'rgba(10,16,30,0.7)',
                transition: 'border 0.2s, background 0.2s',
            }}
        >
            <button
                onClick={() => setOpen(!open)}
                style={{
                    width: '100%', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', padding: '20px 24px', background: 'none',
                    border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px',
                }}
            >
                <span style={{ color: open ? '#60A5FA' : '#E2E8F0', fontSize: '1rem', fontWeight: '600', lineHeight: '1.4' }}>{q}</span>
                <span style={{
                    color: open ? '#3B82F6' : '#475569', flexShrink: 0,
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s, color 0.2s', fontSize: '14px',
                }}>
                    <FaChevronDown />
                </span>
            </button>
            {open && (
                <div style={{ padding: '0 24px 20px', color: '#94A3B8', fontSize: '0.95rem', lineHeight: '1.8' }}>
                    {typeof a === 'string' ? a : a}
                </div>
            )}
        </motion.div>
    );
}

export default function SecurityServicePage({
    title,
    subtitle,
    badge,
    breadcrumb,
    heroImage,
    intro,
    highlights = [],
    processSteps = [],
    features = [],
    statsRow = [],
    extraSection,
    faqs = [],
    accentColor = '#3B82F6',
}) {
    return (
        <>
            {/* ── HERO BANNER ── */}
            <section style={{ position: 'relative', minHeight: '420px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <video autoPlay loop muted playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
                    <source src={HomeVideo} type="video/mp4" />
                </video>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(3,7,18,0.93) 0%, rgba(6,11,30,0.88) 100%)', zIndex: 1 }} />
                <div style={{ position: 'absolute', top: '-80px', left: '5%', width: '500px', height: '500px', borderRadius: '50%', background: `radial-gradient(circle, ${accentColor}22 0%, transparent 70%)`, zIndex: 1 }} />
                <div style={{ position: 'absolute', bottom: '-60px', right: '5%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)', zIndex: 1 }} />

                <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '100px 20px 60px', maxWidth: '800px' }}>
                    <motion.div {...fadeUp(0)}>
                        <span style={{ display: 'inline-block', background: `${accentColor}18`, border: `1px solid ${accentColor}44`, color: accentColor === '#3B82F6' ? '#60A5FA' : accentColor, borderRadius: '20px', padding: '6px 20px', fontSize: '12px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '20px' }}>
                            {badge || 'SECURITY TESTING'}
                        </span>
                        <h1 style={{ color: '#F1F5F9', fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: '800', lineHeight: '1.15', marginBottom: '20px' }}>
                            {title}
                        </h1>
                        {subtitle && <p style={{ color: '#94A3B8', fontSize: '1.05rem', lineHeight: '1.7', maxWidth: '600px', margin: '0 auto 24px' }}>{subtitle}</p>}
                        <nav aria-label="breadcrumb">
                            <ol style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', listStyle: 'none', margin: 0, padding: 0, flexWrap: 'wrap' }}>
                                <li><Link to="/" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '14px' }}>Home</Link></li>
                                <li style={{ color: '#475569', fontSize: '12px' }}>›</li>
                                <li><Link to="/services" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '14px' }}>Services</Link></li>
                                <li style={{ color: '#475569', fontSize: '12px' }}>›</li>
                                <li style={{ color: '#60A5FA', fontSize: '14px', fontWeight: '600' }}>{breadcrumb || title}</li>
                            </ol>
                        </nav>
                    </motion.div>
                </div>
            </section>

            {/* ── INTRO SECTION ── */}
            <section style={{ backgroundColor: '#060B14', padding: '90px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, right: 0, width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-6">
                            <motion.div {...fadeUp(0)}>
                                <span style={{ color: accentColor, fontSize: '12px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '14px', display: 'block' }}>● OVERVIEW</span>
                                <h2 style={{ color: '#F1F5F9', fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', fontWeight: '800', lineHeight: '1.25', marginBottom: '20px' }}>{title}</h2>
                                {intro.map((p, i) => (
                                    <p key={i} style={{ color: '#94A3B8', fontSize: '0.97rem', lineHeight: '1.85', marginBottom: '14px' }}>{p}</p>
                                ))}
                                {highlights.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '24px' }}>
                                        {highlights.map((h, i) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                <FaCheckCircle style={{ color: accentColor, marginTop: '3px', flexShrink: 0 }} />
                                                <span style={{ color: '#CBD5E1', fontSize: '0.92rem' }}>{h}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Link to="/contact-us" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: `linear-gradient(135deg, ${accentColor}, #6366F1)`, color: '#fff', padding: '13px 28px', borderRadius: '10px', textDecoration: 'none', fontWeight: '700', fontSize: '0.95rem', marginTop: '28px' }}>
                                    Get Free Consultation <FaArrowRight style={{ fontSize: '12px' }} />
                                </Link>
                            </motion.div>
                        </div>
                        <div className="col-lg-6">
                            <motion.div {...fadeUp(0.2)} style={{ position: 'relative' }}>
                                <div style={{ borderRadius: '20px', overflow: 'hidden', border: `1px solid ${accentColor}22`, boxShadow: '0 30px 80px rgba(0,0,0,0.5)' }}>
                                    <img src={heroImage} alt={title} style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: '400px' }} />
                                </div>
                                <div style={{ position: 'absolute', top: '-12px', right: '-12px', width: '70px', height: '70px', borderTop: `3px solid ${accentColor}`, borderRight: `3px solid ${accentColor}`, borderRadius: '0 16px 0 0' }} />
                                <div style={{ position: 'absolute', bottom: '-12px', left: '-12px', width: '70px', height: '70px', borderBottom: '3px solid #6366F1', borderLeft: '3px solid #6366F1', borderRadius: '0 0 0 16px' }} />
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── STATS ROW ── */}
            {statsRow.length > 0 && (
                <section style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)', borderTop: `1px solid ${accentColor}22`, borderBottom: `1px solid ${accentColor}22`, padding: '48px 0' }}>
                    <div className="container">
                        <div className="row g-4 text-center">
                            {statsRow.map((s, i) => (
                                <div key={i} className="col-6 col-md-3">
                                    <motion.div {...fadeUp(i * 0.1)}>
                                        <div style={{ color: accentColor, fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: '800', lineHeight: 1 }}>{s.value}</div>
                                        <div style={{ color: '#64748B', fontSize: '0.85rem', marginTop: '6px' }}>{s.label}</div>
                                    </motion.div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── PROCESS STEPS ── */}
            {processSteps.length > 0 && (
                <section style={{ backgroundColor: '#060B14', padding: '90px 0' }}>
                    <div className="container">
                        <motion.div {...fadeUp(0)} className="text-center" style={{ marginBottom: '56px' }}>
                            <span style={{ display: 'inline-block', background: `${accentColor}15`, border: `1px solid ${accentColor}33`, color: '#60A5FA', borderRadius: '20px', padding: '6px 20px', fontSize: '12px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '14px' }}>OUR PROCESS</span>
                            <h2 style={{ color: '#F1F5F9', fontSize: 'clamp(1.7rem, 3.5vw, 2.5rem)', fontWeight: '800' }}>
                                How We <span style={{ color: accentColor }}>Do It</span>
                            </h2>
                        </motion.div>
                        <div className="row g-4">
                            {processSteps.map((step, i) => (
                                <div key={i} className="col-lg-4 col-md-6">
                                    <motion.div
                                        {...fadeUp(i * 0.1)}
                                        whileHover={{ y: -6, transition: { duration: 0.2 } }}
                                        style={{
                                            background: 'rgba(10,16,30,0.8)', border: '1px solid rgba(255,255,255,0.07)',
                                            borderTop: `3px solid ${accentColor}`, borderRadius: '16px', padding: '32px 26px', height: '100%',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.boxShadow = `0 20px 50px rgba(0,0,0,0.4), 0 0 0 1px ${accentColor}22`}
                                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                                            <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: `${accentColor}18`, border: `1px solid ${accentColor}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentColor, fontSize: '18px', flexShrink: 0 }}>
                                                {step.icon || <FaShieldAlt />}
                                            </div>
                                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: `${accentColor}18`, border: `1px solid ${accentColor}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentColor, fontSize: '12px', fontWeight: '800' }}>
                                                {String(i + 1).padStart(2, '0')}
                                            </div>
                                        </div>
                                        <h3 style={{ color: '#F1F5F9', fontSize: '1.05rem', fontWeight: '700', marginBottom: '10px' }}>{step.title}</h3>
                                        <p style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: '1.75', margin: 0 }}>{step.desc}</p>
                                    </motion.div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── FEATURES / WHY US ── */}
            {features.length > 0 && (
                <section style={{ position: 'relative', padding: '90px 0', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${securityBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', zIndex: 0 }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(3,7,18,0.94) 0%, rgba(6,11,30,0.92) 100%)', zIndex: 1 }} />
                    <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                        <motion.div {...fadeUp(0)} className="text-center" style={{ marginBottom: '56px' }}>
                            <span style={{ display: 'inline-block', background: `${accentColor}15`, border: `1px solid ${accentColor}33`, color: '#60A5FA', borderRadius: '20px', padding: '6px 20px', fontSize: '12px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '14px' }}>WHY CHOOSE US</span>
                            <h2 style={{ color: '#F1F5F9', fontSize: 'clamp(1.7rem, 3.5vw, 2.5rem)', fontWeight: '800' }}>
                                Key <span style={{ color: accentColor }}>Capabilities</span>
                            </h2>
                        </motion.div>
                        <div className="row g-4">
                            {features.map((f, i) => (
                                <div key={i} className="col-lg-4 col-md-6">
                                    <motion.div
                                        {...fadeUp(i * 0.1)}
                                        whileHover={{ y: -6, transition: { duration: 0.2 } }}
                                        style={{
                                            background: 'rgba(10,16,30,0.82)', backdropFilter: 'blur(12px)',
                                            border: '1px solid rgba(255,255,255,0.06)', borderLeft: `3px solid ${f.color || accentColor}`,
                                            borderRadius: '16px', padding: '28px 24px', height: '100%',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.boxShadow = `0 20px 50px rgba(0,0,0,0.4)`}
                                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                                    >
                                        <div style={{ fontSize: '2rem', marginBottom: '16px' }}>{f.icon}</div>
                                        <div style={{ width: '24px', height: '2px', background: f.color || accentColor, borderRadius: '2px', marginBottom: '12px' }} />
                                        <h3 style={{ color: '#F1F5F9', fontSize: '1rem', fontWeight: '700', marginBottom: '10px' }}>{f.title}</h3>
                                        <p style={{ color: '#64748B', fontSize: '0.88rem', lineHeight: '1.75', margin: 0 }}>{f.desc}</p>
                                    </motion.div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── EXTRA SECTION (optional slot) ── */}
            {extraSection}

            {/* ── FAQ ── */}
            {faqs.length > 0 && (
                <section style={{ backgroundColor: '#060B14', padding: '90px 0' }}>
                    <div className="container">
                        <motion.div {...fadeUp(0)} className="text-center" style={{ marginBottom: '56px' }}>
                            <span style={{ display: 'inline-block', background: `${accentColor}15`, border: `1px solid ${accentColor}33`, color: '#60A5FA', borderRadius: '20px', padding: '6px 20px', fontSize: '12px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '14px' }}>SUPPORT</span>
                            <h2 style={{ color: '#F1F5F9', fontSize: 'clamp(1.7rem, 3.5vw, 2.5rem)', fontWeight: '800', marginBottom: '12px' }}>
                                Frequently Asked <span style={{ color: accentColor }}>Questions</span>
                            </h2>
                            <p style={{ color: '#64748B', fontSize: '1rem', maxWidth: '500px', margin: '0 auto' }}>Everything you need to know about our service.</p>
                        </motion.div>
                        <div className="row justify-content-center">
                            <div className="col-lg-9">
                                {faqs.map((faq, i) => (
                                    <FAQItem key={i} index={i} q={faq.q} a={faq.a} />
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ── CTA BANNER ── */}
            <section style={{ background: `linear-gradient(135deg, #0F172A 0%, #1E293B 100%)`, borderTop: `1px solid ${accentColor}22`, padding: '70px 0', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '600px', height: '300px', borderRadius: '50%', background: `radial-gradient(circle, ${accentColor}14 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <div className="container" style={{ position: 'relative', zIndex: 1 }}>
                    <motion.div {...fadeUp(0)}>
                        <h2 style={{ color: '#F1F5F9', fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: '800', marginBottom: '14px' }}>
                            Ready to <span style={{ color: accentColor }}>Secure Your Systems?</span>
                        </h2>
                        <p style={{ color: '#64748B', fontSize: '1rem', maxWidth: '480px', margin: '0 auto 32px', lineHeight: '1.7' }}>
                            Get a free consultation and let our certified experts assess your security posture today.
                        </p>
                        <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Link to="/contact-us" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: `linear-gradient(135deg, ${accentColor}, #6366F1)`, color: '#fff', padding: '14px 32px', borderRadius: '10px', textDecoration: 'none', fontWeight: '700', fontSize: '1rem', boxShadow: `0 8px 24px ${accentColor}44` }}>
                                Contact Us Now <FaArrowRight style={{ fontSize: '12px' }} />
                            </Link>
                            <Link to="/services" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', color: '#CBD5E1', padding: '14px 32px', borderRadius: '10px', textDecoration: 'none', fontWeight: '600', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.1)' }}>
                                All Services
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            <FooterContact />
        </>
    );
}
